import React, { useMemo } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';
import { SEV_COLORS, CARD_SURFACE_LIGHT, CARD_SURFACE_DARK } from './constants';
import { lineEndLabelsPlugin, deliveryRulesPlugin } from './chartPlugins';
import {
  buildClientTimeline,
  formatShortDate,
  formatFullDate,
  PLOTTED_SEVERITIES,
} from './trajectoryTimeline';
import { ProjectRemediationSeries } from '../../Models/Retest';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

interface SeverityLinesChartProps {
  series: ProjectRemediationSeries[];
}

/**
 * One line per risk level, each summing that severity across every delivered project.
 *
 * The only view that lets a client watch Criticals on their own: inside a stacked total of thirty,
 * four Criticals are invisible. Drawn point to point rather than stepped — findings are fixed
 * gradually between tests and the retest only records where they landed, so a slope reads truer
 * than a cliff, and four staircases crossing at right angles is far harder to follow than four
 * diagonals. A marker sits on every test so it stays clear that the points are the measurements
 * and the segments between them are only the join.
 */
const SeverityLinesChart: React.FC<SeverityLinesChartProps> = ({ series }) => {
  const { textColor, gridColor, isDark } = useChartTheme();
  const surface = isDark ? CARD_SURFACE_DARK : CARD_SURFACE_LIGHT;

  const timeline = useMemo(() => buildClientTimeline(series), [series]);

  const deliveryIndices = useMemo(
    () => timeline.reduce<number[]>((acc, e, i) => (e.kind === 'delivery' ? [...acc, i] : acc), []),
    [timeline]
  );

  const chartData = {
    labels: timeline.map(e => formatShortDate(e.date)),
    datasets: PLOTTED_SEVERITIES.map(({ key, label }) => ({
      label,
      data: timeline.map(e => e.counts[key] ?? 0),
      borderColor: SEV_COLORS[label],
      backgroundColor: SEV_COLORS[label],
      pointBackgroundColor: SEV_COLORS[label],
      pointBorderColor: surface,
      pointBorderWidth: 1.5,
      pointRadius: 3.5,
      pointHoverRadius: 5.5,
      borderWidth: 2,
      tension: 0,
      fill: false,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Room on the right for the direct end-labels.
    layout: { padding: { right: 78, top: 8 } },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { color: textColor, font: { size: 11 }, boxWidth: 11, boxHeight: 11 },
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => (timeline[items[0]?.dataIndex] ? formatFullDate(timeline[items[0].dataIndex].date) : ''),
          beforeBody: (items: any[]) => timeline[items[0]?.dataIndex]?.label ?? '',
          footer: (items: any[]) => `Total open  ${items.reduce((a, i) => a + Number(i.parsed.y ?? 0), 0)}`,
        },
      },
      lineEndLabels: { color: textColor },
      deliveryRules: { indices: deliveryIndices, color: gridColor },
    },
    scales: {
      x: {
        type: 'category' as const,
        grid: { display: false },
        ticks: { color: textColor, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 11 }, precision: 0 },
      },
    },
  };

  return (
    <ChartCard
      title="Open findings by risk level"
      sub="All delivered projects · each line is one severity · upright rules mark deliveries"
    >
      {timeline.length === 0 ? (
        <div className="text-muted text-center py-5 small">
          No delivered projects to show a trajectory for yet
        </div>
      ) : (
        <div data-testid="severity-lines-plot" style={{ height: 300, width: '100%' }}>
          <Line data={chartData} options={options} plugins={[lineEndLabelsPlugin, deliveryRulesPlugin]} />
        </div>
      )}
    </ChartCard>
  );
};

export default SeverityLinesChart;
